// src/components/Registros/forms/HorasExtrasForm.jsx

import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaClock } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AsyncSelect from 'react-select/async';
import { useNavigate } from 'react-router-dom';
import SelectMult from '../../SettingNot/SelectMult';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos, actualizarDatos, filtroOTTexto } from '../../../api/common';

/**
 * Opción visible del AsyncSelect de OT.
 * `label` = "#1234 — Cliente Pepe (Bogotá)"
 */
const toOtOption = (ot) => ({
  value: ot._id,
  label: `#${ot.numeroOrden || '-'} — ${ot.cliente?.nombre || 'Sin cliente'}${ot.cliente?.ciudad ? ` (${ot.cliente.ciudad})` : ''}`,
  ot,
});

/** Fetch de OTs padre por texto para el AsyncSelect (debounced vía react-select). */
const buscarOts = async (inputValue) => {
  const texto = (inputValue || '').trim();
  if (texto.length < 2) return [];
  try {
    const res = await filtroOTTexto({ searchText: texto, limit: 20 });
    const list = res?.data?.data || res?.data || [];
    return (Array.isArray(list) ? list : []).map(toOtOption);
  } catch (err) {
    console.error('[HorasExtrasForm] error buscando OTs:', err);
    return [];
  }
};

const HorasExtrasForm = ({ onClose, onSubmit, tipoRegistro, solicitante, registroInicial = null }) => {
  const esEdicion = Boolean(registroInicial?._id);
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => ({
    solicitante: solicitante,
    titulo: registroInicial?.titulo || '',
    fechaInicio: registroInicial?.fechaInicio ? registroInicial.fechaInicio.slice(0, 10) : '',
    fechaFin: registroInicial?.fechaFin ? registroInicial.fechaFin.slice(0, 10) : '',
    horaInicio: registroInicial?.horaInicio || '',
    horaFin: registroInicial?.horaFin || '',
    horasSolicitadas: registroInicial?.horasSolicitadas || '',
    tipoHoraExtra: registroInicial?.tipoHoraExtra || 'diurna',
    actividad: registroInicial?.actividad || '',
    justificacion: registroInicial?.justificacion || '',
    descripcion: registroInicial?.descripcion || '',
    urgencia: registroInicial?.urgencia || 'media',
    responsablesAprobacion: registroInicial?.responsablesAprobacion || [],
    ordenTrabajoAsociada: registroInicial?.ordenTrabajoAsociada?._id || registroInicial?.ordenTrabajoAsociada || null,
    adjuntos: registroInicial?.adjuntos || [],
  }));

  // Opción seleccionada del AsyncSelect (solo UI; el id se guarda en formData.ordenTrabajoAsociada)
  const [otSeleccionada, setOtSeleccionada] = useState(
    registroInicial?.ordenTrabajoAsociada
      ? toOtOption(registroInicial.ordenTrabajoAsociada)
      : null
  );

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Calcula las horas entre la hora inicial y final; admite jornadas que terminan al día siguiente.
  useEffect(() => {
    if (!formData.horaInicio || !formData.horaFin) {
      setFormData(prev => ({ ...prev, horasSolicitadas: '' }));
      return;
    }

    const [inicioH, inicioM] = formData.horaInicio.split(':').map(Number);
    const [finH, finM] = formData.horaFin.split(':').map(Number);
    const inicio = inicioH * 60 + inicioM;
    let fin = finH * 60 + finM;
    if (fin <= inicio) fin += 24 * 60;

    const horas = (fin - inicio) / 60;
    setFormData(prev => ({ ...prev,
        horasSolicitadas: Number(horas.toFixed(2)),
        fechaFin: formData.fechaInicio // Asignar fechaFin igual a fechaInicio
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.horaInicio, formData.horaFin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target;
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    })) || [];

    setFormData(prev => ({ ...prev, responsablesAprobacion: responsablesFormateados }));
    if (responsablesFormateados.length > 0) {
      setErrors(prev => ({ ...prev, responsablesAprobacion: '' }));
    }
  };

  const handleOtChange = (option) => {
    setOtSeleccionada(option);
    setFormData(prev => ({ ...prev, ordenTrabajoAsociada: option?.value || null }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera a partir del tipo de hora extra (ver handleSubmit).
    if (!formData.fechaInicio) newErrors.fechaInicio = 'La fecha de las horas extras es requerida';
    if (!formData.horaInicio) newErrors.horaInicio = 'La hora de inicio es requerida';
    if (!formData.horaFin) newErrors.horaFin = 'La hora de finalización es requerida';
    if (formData.horaInicio && formData.horaFin && !formData.horasSolicitadas) {
      newErrors.horaFin = 'La hora de finalización debe ser diferente a la hora de inicio';
    }
    if (Number(formData.horasSolicitadas) > 12) {
      newErrors.horaFin = 'La duración calculada no puede superar 12 horas por solicitud';
    }
    if (!formData.actividad.trim()) newErrors.actividad = 'La actividad es requerida';
    if (!formData.justificacion.trim()) newErrors.justificacion = 'La justificación es requerida';
    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    // Si no asoció OT, pedir confirmación explícita.
    if (!formData.ordenTrabajoAsociada) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Sin orden de trabajo asociada',
        html: '¿Deseas crear la solicitud <strong>sin</strong> asociar una Orden de Trabajo?',
        showCancelButton: true,
        confirmButtonText: 'Sí, crear sin OT',
        cancelButtonText: 'Volver',
        confirmButtonColor: '#0d6efd',
        cancelButtonColor: '#6c757d',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      // Título automático en base al tipo de hora extra — reemplaza al input
      // visible, se usa en /Solicitudes y en el calendario para describir la fila.
      const tituloAuto = `Horas Extras ${formData.tipoHoraExtra || ''}`.trim();
      const dataToSubmit = {
        ...formData,
        titulo: tituloAuto,
        tipoSolicitud: 'horasExtras',
        causaInactividad: false
      };

      if (esEdicion) {
        await actualizarDatos('Registro', dataToSubmit, registroInicial._id);
      } else {
        await crearDatos('Registro', dataToSubmit);
      }
      await Swal.fire({
        icon: 'success',
        title: esEdicion ? 'Solicitud actualizada' : 'Solicitud Enviada',
        text: esEdicion
          ? 'Tu solicitud ha sido actualizada correctamente'
          : 'Tu solicitud de horas extras ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      // Al cerrar el formulario con éxito, redirigimos al listado — tanto si
      // es creación nueva como edición desde /Solicitudes.
      navigate('/Solicitudes');
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) onClose();
    });
  };

  const getUrgenciaBadge = (urgencia) => {
    const badges = {
      baja: { color: '#6c757d', text: 'Baja' },
      media: { color: '#0d6efd', text: 'Media' },
      alta: { color: '#ffc107', text: 'Alta' },
      critica: { color: '#dc3545', text: 'Crítica' }
    };
    return badges[urgencia] || badges.media;
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #495057 0%, #343a40 100%)' }}>
          <div className="d-flex align-items-center">
            <Button variant="light" size="sm" onClick={handleCancel} className="me-3" disabled={loading}>
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">{esEdicion ? 'Editar Solicitud de Horas Extras' : 'Solicitud de Horas Extras'}</h3>
              <p className="mb-0 opacity-75">Registra el tiempo adicional requerido y las actividades que se realizarán</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Alert variant="info" className="d-flex align-items-center">
            <FaClock className="me-2" size={20} />
            <div>
              <strong>Importante:</strong> registra la fecha, el horario y las actividades que justifican el tiempo adicional.
              Las horas se calculan automáticamente a partir de la hora de inicio y finalización.
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Urgencia *</Form.Label>
                    <Form.Select name="urgencia" value={formData.urgencia} onChange={handleChange}>
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                      <option value="critica">Crítica</option>
                    </Form.Select>
                    <div className="mt-2">
                      <span className="badge" style={{ backgroundColor: getUrgenciaBadge(formData.urgencia).color, color: 'white' }}>
                        {getUrgenciaBadge(formData.urgencia).text}
                      </span>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de las Horas Extras *</Form.Label>
                    <Form.Control type="date" name="fechaInicio" value={formData.fechaInicio} onChange={handleChange} isInvalid={!!errors.fechaInicio} />
                    <Form.Control.Feedback type="invalid">{errors.fechaInicio}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Tipo de Hora Extra *</Form.Label>
                    <Form.Select name="tipoHoraExtra" value={formData.tipoHoraExtra} onChange={handleChange}>
                      <option value="diurna">Diurna</option>
                      <option value="nocturna">Nocturna</option>
                      <option value="dominical">Dominical</option>
                      <option value="festiva">Festiva</option>
                      <option value="dominicalNocturna">Dominical nocturna</option>
                      <option value="festivaNocturna">Festiva nocturna</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Hora de Inicio *</Form.Label>
                    <Form.Control type="time" name="horaInicio" value={formData.horaInicio} onChange={handleChange} isInvalid={!!errors.horaInicio} />
                    <Form.Control.Feedback type="invalid">{errors.horaInicio}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Hora de Finalización *</Form.Label>
                    <Form.Control type="time" name="horaFin" value={formData.horaFin} onChange={handleChange} isInvalid={!!errors.horaFin} />
                    <Form.Control.Feedback type="invalid">{errors.horaFin}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Total de Horas</Form.Label>
                    <Form.Control type="number" value={formData.horasSolicitadas} readOnly placeholder="Se calcula automáticamente" />
                    <Form.Text className="text-muted">Calculado según el horario ingresado.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Orden de Trabajo (opcional)</Form.Label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={false}
                      loadOptions={buscarOts}
                      value={otSeleccionada}
                      onChange={handleOtChange}
                      isClearable
                      placeholder="Buscar por número, cliente o ciudad..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue?.length < 2 ? 'Escribe al menos 2 caracteres…' : 'Sin coincidencias'
                      }
                      loadingMessage={() => 'Buscando OTs...'}
                    />
                    <Form.Text className="text-muted">
                      Opcional. Si se omite, se pedirá confirmación al enviar la solicitud.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Actividades y Justificación</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Actividad que se realizará *</Form.Label>
                <Form.Control as="textarea" rows={4} name="actividad" value={formData.actividad} onChange={handleChange}
                  placeholder="Describe las tareas específicas que se realizarán durante las horas extras..." isInvalid={!!errors.actividad} maxLength={800} />
                <Form.Control.Feedback type="invalid">{errors.actividad}</Form.Control.Feedback>
                <Form.Text className="text-muted">{formData.actividad.length}/800 caracteres</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Justificación de la Solicitud *</Form.Label>
                <Form.Control as="textarea" rows={4} name="justificacion" value={formData.justificacion} onChange={handleChange}
                  placeholder="Explica por qué estas actividades requieren tiempo adicional y por qué no pueden realizarse en la jornada habitual..."
                  isInvalid={!!errors.justificacion} maxLength={500} />
                <Form.Control.Feedback type="invalid">{errors.justificacion}</Form.Control.Feedback>
                <Form.Text className="text-muted">{formData.justificacion.length}/500 caracteres</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Observaciones Adicionales</Form.Label>
                <Form.Control as="textarea" rows={3} name="descripcion" value={formData.descripcion} onChange={handleChange}
                  placeholder="Información adicional relevante..." maxLength={500} />
                <Form.Text className="text-muted">{formData.descripcion.length}/500 caracteres</Form.Text>
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="horasExtras"
                registroId={registroInicial?._id || null}
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                  handledata={handleResponsableChange}
                  name="responsablesAprobacion"
                  collection="Usuarios"
                  field="nombre"
                  baseFilter={[
                    { $match: { status: 'activo' } },
                    { $lookup: { from: 'roles', localField: 'role', foreignField: '_id', as: 'result' } },
                    { $match: { 'result.nombre': { $ne: 'Cliente' } } }
                  ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">{errors.responsablesAprobacion}</Alert>
                )}
              </Form.Group>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={handleCancel} disabled={loading} className="px-4">
                <FaTimes className="me-2" />Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading} className="px-4">
                {loading ? <><span className="spinner-border spinner-border-sm me-2" />Enviando...</> : <><FaSave className="me-2" />{esEdicion ? 'Actualizar Solicitud' : 'Enviar Solicitud'}</>}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default HorasExtrasForm;
